// Hook for connecting mock WebSocket to Redux timeline slice
// For Storybook testing only

import {
  appendMessageText,
  finalizeMessage,
  selectActiveConvId,
  updateToolProgress,
} from '@platform/timeline';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { MockWs } from '../../../services/chatWs.mock';

export interface UseMockChatStreamProps {
  mockWs: MockWs;
  enabled?: boolean;
}

export function useMockChatStream({ mockWs, enabled = true }: UseMockChatStreamProps) {
  const dispatch = useDispatch();
  const convId = useSelector(selectActiveConvId);

  useEffect(() => {
    if (!enabled || !convId) return;

    const unsubscribe = mockWs.subscribe((event) => {
      switch (event.type) {
        case 'token': {
          if (event.messageId && event.text) {
            dispatch(
              appendMessageText({
                convId,
                messageId: event.messageId,
                text: event.text,
              })
            );
          }
          break;
        }

        case 'final': {
          if (event.messageId) {
            dispatch(
              finalizeMessage({
                convId,
                messageId: event.messageId,
                fullText: event.text,
              })
            );
          }
          break;
        }

        case 'tool-status': {
          if (event.toolRunId && event.status) {
            dispatch(
              updateToolProgress({
                convId,
                toolId: event.toolRunId,
                progress: event.progress ?? 0,
                status: event.status as 'error' | 'completed' | 'running' | 'queued',
              })
            );
          }
          break;
        }

        default:
          console.debug('Unhandled mock WS event:', event.type);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [mockWs, dispatch, convId, enabled]);

  return mockWs;
}
